import { JsonBadge } from "@/components/JsonBadge";
import { ColumnType, ReportCell, ReportColumn } from "@/types/evaluate";
import { forceJSONString } from "@/utils/strings";
import { CheckCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { CompareDiffCell } from "./CompareDiffCell";
import { FunctionCallCell } from "./FunctionCallCell";
import { AssertionsCell } from "./AssertionsCell";
import { CellActions } from "./CellActions";
import {
  BooleanIcon,
  getBooleanColors,
} from "@/components/ui/table/core/BooleanCell";

const renderCellValue = (
  cell: ReportCell,
  column: ReportColumn,
  setCopyValue: (value: string) => void,
) => {
  let cellValue = null;

  switch (cell.display_value.type) {
    case "string":
      cellValue = cell.display_value.value;
      setCopyValue(cell.display_value.value);
      break;
    case "number":
      const value =
        column.column_type === ColumnType.ABSOLUTE_NUMERIC_DISTANCE ||
        column.column_type === ColumnType.COSINE_SIMILARITY
          ? Number(cell.display_value.value).toFixed(2)
          : cell.display_value.value;
      cellValue = <span>{value}</span>;
      setCopyValue(cell.display_value.value.toString());
      break;
    case "function":
      cellValue = <FunctionCallCell cell={cell} />;
      setCopyValue(JSON.stringify(cell.display_value.value, null, 2));
      break;
    case "tool_calls":
      cellValue = <FunctionCallCell cell={cell} />;
      setCopyValue(JSON.stringify(cell.display_value.value, null, 2));
      break;
    case "object":
      setCopyValue(JSON.stringify(cell.display_value.value, null, 2));
      if (column.column_type === ColumnType.LLM_ASSERTION) {
        cellValue = <AssertionsCell value={cell.display_value.value} />;

        break;
      }

      cellValue = (
        <pre className={`whitespace-pre-wrap font-mono text-gray-800`}>
          <JsonBadge />
          {forceJSONString(cell.display_value.value)}
        </pre>
      );

      break;
    case "boolean":
      if (column.column_type === ColumnType.LLM_ASSERTION) {
        const prompt =
          typeof column.configuration.prompt === "string" &&
          column.configuration.prompt.startsWith("[")
            ? JSON.parse(column.configuration.prompt)[0]
            : column.configuration.prompt;
        setCopyValue(
          JSON.stringify(
            prompt
              ? { [prompt]: cell.display_value.value }
              : cell.display_value.value,
            null,
            2,
          ),
        );
        cellValue = (
          <AssertionsCell prompt={prompt} value={cell.display_value.value} />
        );
        break;
      }

      setCopyValue(cell.display_value.value ? "true" : "false");

      if (column.column_type === ColumnType.COMPARE) {
        if (!cell.display_value.value) {
          cellValue = (
            <CompareDiffCell key={`${cell?.id}-compare`} cell={cell} />
          );
        } else {
          cellValue = (
            <span className="flex items-center justify-center text-gray-400">
              <CheckCircle className="mr-1 h-4 w-4 text-green-300" /> Values are
              the same
            </span>
          );
        }
      } else {
        cellValue = (
          <span
            className={`${getBooleanColors(
              cell.display_value.value as boolean,
            )} flex h-full min-h-[50px] w-full items-center justify-center`}
          >
            <BooleanIcon value={cell.display_value.value as boolean} />
          </span>
        );
      }
      break;
    case "unknown":
      if (column.column_type === ColumnType.COMPARE) {
        return <CompareDiffCell key={`${cell?.id}-compare`} cell={cell} />;
      }
      cellValue = <span className="flex justify-center text-gray-300">—</span>;
      setCopyValue("");
      break;
  }

  return cellValue;
};

export const CompletedCell = ({
  cell,
  column,
  retryReportCell,
  retryReportCellIsLoading,
}: {
  cell: ReportCell;
  column: ReportColumn;
  retryReportCell: () => void;
  retryReportCellIsLoading: boolean;
}) => {
  const [copyValue, setCopyValue] = useState("");

  const cellValue = useMemo(
    () => renderCellValue(cell, column, setCopyValue),
    [cell, column, setCopyValue],
  );

  const isBoolean = cell.display_value.type === "boolean";

  return (
    <>
      <CellActions
        cell={cell}
        column={column}
        retryReportCell={retryReportCell}
        retryReportCellIsLoading={retryReportCellIsLoading}
        copyValue={copyValue}
      />
      <div className="group relative flex h-full max-h-[250px] overflow-auto">
        <div
          className={`flex h-full min-h-[32px] w-full whitespace-pre-wrap break-words ${
            column.column_type === ColumnType.LLM_ASSERTION || isBoolean
              ? ""
              : "my-auto px-2 py-3"
          }`}
        >
          {cellValue}
        </div>
      </div>
    </>
  );
};
